import { createContext, useContext, useReducer, useEffect } from 'react';

const AppContext = createContext();

// App state reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'SET_SIDEBAR_COLLAPSED':
      return { ...state, sidebarCollapsed: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_MODULES':
      return { ...state, modules: action.payload };
    case 'SET_BRANCHES':
      return { ...state, branches: action.payload };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_SUBCATEGORIES':
      return { ...state, subcategories: action.payload };
    case 'UPDATE_STATS':
      return { ...state, stats: { ...state.stats, ...action.payload } };
    default:
      return state;
  }
};

const initialState = {
  theme: 'light',
  sidebarCollapsed: false,
  isLoading: false,
  modules: [],
  branches: [],
  categories: [],
  subcategories: [],
  stats: {
    totalTickets: 0,
    openTickets: 0,
    closedTickets: 0,
    myTickets: 0
  }
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('rdx_theme');
    if (savedTheme) {
      dispatch({ type: 'SET_THEME', payload: savedTheme });
    }
  }, []);

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('rdx_theme', state.theme);

    // Apply theme to document
    if (state.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.theme]);

  // Actions
  const setTheme = theme => {
    dispatch({ type: 'SET_THEME', payload: theme });
  };

  const toggleTheme = () => {
    setTheme(state.theme === 'light' ? 'dark' : 'light');
  };

  const setSidebarCollapsed = collapsed => {
    dispatch({ type: 'SET_SIDEBAR_COLLAPSED', payload: collapsed });
  };

  const setLoading = loading => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const setModules = modules => {
    dispatch({ type: 'SET_MODULES', payload: modules });
  };

  const setBranches = branches => {
    dispatch({ type: 'SET_BRANCHES', payload: branches });
  };

  const setCategories = categories => {
    dispatch({ type: 'SET_CATEGORIES', payload: categories });
  };

  const setSubcategories = subcategories => {
    dispatch({ type: 'SET_SUBCATEGORIES', payload: subcategories });
  };

  const updateStats = stats => {
    dispatch({ type: 'UPDATE_STATS', payload: stats });
  };

  const value = {
    ...state,
    setTheme,
    toggleTheme,
    setSidebarCollapsed,
    setLoading,
    setModules,
    setBranches,
    setCategories,
    setSubcategories,
    updateStats
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
